/**
 * Bounded i18n boot.
 *
 * The app used to `await initializeI18n()` BEFORE the first `root.render(...)`,
 * so anything that stalled the i18n network calls (a hung
 * `/api/i18n/languages`, or i18next's exponential-backoff retries on an aborted
 * namespace fetch) left `#root` completely EMPTY — a blank white page on
 * `/login`, with no error boundary and no way in.
 *
 * Rendering the UI is never allowed to depend on the network: this helper gives
 * initialization a budget, then paints regardless, seeding the bundled critical
 * fallback so the login surface is readable. i18next keeps loading in the
 * background and components re-render when the real catalog lands
 * (`bindI18n: 'loaded'` / `bindI18nStore: 'added removed'`).
 */

import { logger } from '@/shared/utils/logger'

/** How long the first paint may wait for the backend catalog. */
const I18N_BOOT_BUDGET_MS = 3000

export interface I18nBootOutcome {
  /** True when i18next finished loading every namespace within the budget. */
  readonly initializedWithinBudget: boolean
  /** `lng:ns` pairs served from the bundled fallback because the backend did not. */
  readonly seededFallbackNamespaces: readonly string[]
  /** True when the app can render readable text now (loaded OR bundled fallback). */
  readonly translationsUsable: boolean
}

export interface I18nBootParams {
  /** The real i18next initialization (network-bound). */
  readonly initializeI18n: () => Promise<unknown>
  /** Seeds the bundled fallback for namespaces the backend did not deliver. */
  readonly applyCriticalFallback: () => string[]
  readonly budgetMs?: number
}

/**
 * Resolves as soon as i18n is ready OR the budget expires — never rejects, so
 * a broken backend can never stop the app from rendering.
 */
export const bootI18nWithinBudget = async ({
  initializeI18n,
  applyCriticalFallback,
  budgetMs = I18N_BOOT_BUDGET_MS,
}: I18nBootParams): Promise<I18nBootOutcome> => {
  let budgetTimer: ReturnType<typeof setTimeout> | undefined

  const initialization = initializeI18n()
    .then(() => 'initialized' as const)
    .catch((error: unknown) => {
      logger.error('[i18n] Initialization failed; continuing with bundled fallback:', error)
      return 'failed' as const
    })

  const budgetExpiry = new Promise<'budget-expired'>((resolve) => {
    budgetTimer = setTimeout(() => resolve('budget-expired'), budgetMs)
  })

  const bootResult = await Promise.race([initialization, budgetExpiry])
  if (budgetTimer !== undefined) clearTimeout(budgetTimer)

  if (bootResult === 'budget-expired') {
    logger.error(
      `[i18n] Translations not ready after ${budgetMs}ms — rendering with the bundled fallback`,
    )
  }
  const initializedWithinBudget = bootResult === 'initialized'

  // Always run: a single failed namespace is repaired the same way a full
  // timeout is, and it is a no-op once everything loaded.
  const seededFallbackNamespaces = applyCriticalFallback()

  return {
    initializedWithinBudget,
    seededFallbackNamespaces,
    translationsUsable: initializedWithinBudget || seededFallbackNamespaces.length > 0,
  }
}
